import { useEffect, useState } from 'react';
import { ChevronDown, Loader2 } from 'lucide-react';
import {
  ContractAbiDetailsErrorCode,
  type NetworkType,
} from '@mls-wallet/shared-types';
import { useContractAbi } from '@/hooks/transaction/use-contract-abi';
import { useListAddressBookEntries } from '@/hooks/organization/use-list-address-book';
import { formatAddress } from '@/lib/utils';
import { AddressBookSelectorDialog } from '@/components/shared/address-book-selector-dialog';
import type { Contract } from './types';

interface ContractAddressInputProps {
  network: NetworkType;
  /** Initial contract to restore from store (used when navigating back) */
  initialContract?: Contract | null;
  onContractLoaded: (contract: Contract) => void;
  /** Called when user clears/edits the address (resets to editing state) */
  onContractCleared?: () => void;
}

/**
 * Contract picker for free-form contract interaction transactions.
 *
 * Even when the policy allows any contract, we still route selection through
 * the shared address-book dialog so contract picks follow the same saved-entry
 * flow as other address selectors.
 */
export function ContractAddressInput({
  network,
  initialContract,
  onContractLoaded,
  onContractCleared,
}: ContractAddressInputProps): React.ReactElement {
  const [open, setOpen] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState<string | null>(
    initialContract?.address ?? null
  );
  const [selectedLabel, setSelectedLabel] = useState<string | null>(
    initialContract?.contractName ?? null
  );

  useEffect(() => {
    setSelectedAddress(initialContract?.address ?? null);
    setSelectedLabel(initialContract?.contractName ?? null);
  }, [initialContract]);

  const {
    entries: addressBookEntries,
    hasMore,
    isLoadingMore,
    loadMore,
  } = useListAddressBookEntries({
    enabled: selectedAddress !== null,
    type: 'CONTRACT',
    pagination: { limit: 100 },
  });

  const matchingEntry = selectedAddress
    ? addressBookEntries.find(
        (entry) => entry.address.toLowerCase() === selectedAddress.toLowerCase()
      )
    : undefined;

  useEffect(() => {
    if (
      !selectedAddress ||
      selectedLabel ||
      matchingEntry ||
      !hasMore ||
      isLoadingMore
    ) {
      return;
    }

    void loadMore();
  }, [
    hasMore,
    isLoadingMore,
    loadMore,
    matchingEntry,
    selectedAddress,
    selectedLabel,
  ]);

  const {
    contractDisplayName,
    implementationFunctions,
    proxyFunctions,
    isLoading,
    errorCode,
    error: networkError,
  } = useContractAbi(
    selectedAddress ? { address: selectedAddress, networkType: network } : null
  );

  const displayError = (() => {
    if (!selectedAddress) return null;
    if (errorCode === ContractAbiDetailsErrorCode.UNVERIFIED_CONTRACT) {
      return 'Unverified contracts are not currently supported.';
    }
    if (errorCode || networkError) {
      return 'Failed to fetch contract ABI';
    }
    return null;
  })();

  useEffect(() => {
    if (!selectedAddress || !contractDisplayName || isLoading || displayError) {
      return;
    }

    onContractLoaded({
      address: selectedAddress,
      contractName:
        selectedLabel ||
        matchingEntry?.name ||
        contractDisplayName ||
        selectedAddress,
      implementationFunctions,
      proxyFunctions,
      allowedProxyFunctions: [],
      allowedImplementationFunctions: [],
    });
  }, [
    contractDisplayName,
    displayError,
    implementationFunctions,
    isLoading,
    matchingEntry?.name,
    onContractLoaded,
    proxyFunctions,
    selectedAddress,
    selectedLabel,
  ]);

  const handleAddressSelect = (address: string, label: string) => {
    if (address !== selectedAddress) {
      onContractCleared?.();
    }
    setSelectedAddress(address);
    setSelectedLabel(label);
  };

  const handleAddressClear = () => {
    setSelectedAddress(null);
    setSelectedLabel(null);
    onContractCleared?.();
  };

  const displayName =
    selectedLabel ||
    matchingEntry?.name ||
    initialContract?.contractName ||
    contractDisplayName ||
    'Select a contract';

  return (
    <>
      <div className="flex min-w-0 max-w-full flex-col">
        {selectedAddress ? (
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="bg-white border border-solid border-zinc-100 rounded-[36px] w-[386px] max-w-full overflow-clip p-[8px] text-left transition-colors hover:bg-slate-100 dark:hover:bg-slate-800 hover:border-slate-200 dark:hover:border-slate-700"
          >
            <div className="flex items-center justify-between gap-[10px] pl-[5px] pr-[5px] py-0">
              <div className="flex min-w-0 flex-1 items-center justify-between gap-2">
                <p className="truncate font-medium text-[14px] text-zinc-900 dark:text-white leading-[20px]">
                  {displayName}
                </p>
                <p className="shrink-0 font-mono text-[12px] text-zinc-500 leading-[24px]">
                  {formatAddress(selectedAddress)}
                </p>
              </div>
              <div className="flex items-center gap-[8px] shrink-0">
                {isLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin text-zinc-400" />
                ) : null}
                <ChevronDown className="size-6 text-zinc-600 dark:text-zinc-400" />
              </div>
            </div>
          </button>
        ) : (
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="bg-white border border-solid border-zinc-100 rounded-[36px] w-[386px] max-w-full overflow-clip p-[8px] text-left transition-colors hover:bg-slate-100 dark:hover:bg-slate-800 hover:border-slate-200 dark:hover:border-slate-700"
          >
            <div className="flex items-center justify-between pl-[5px] pr-[5px] py-0">
              <p className="font-medium text-[14px] text-zinc-500 leading-[20px] px-[12px]">
                Select a contract
              </p>
              <ChevronDown className="w-[24px] h-[24px] text-black shrink-0" />
            </div>
          </button>
        )}

        {displayError ? (
          <p className="text-red-500 text-[12px] mt-[4px] pl-[12px]">
            {displayError}
          </p>
        ) : null}
      </div>

      <AddressBookSelectorDialog
        open={open}
        onOpenChange={setOpen}
        mode="single"
        title="Select contract"
        description="Choose a contract from the address book, or save a new contract address before using it."
        selectedAddress={selectedAddress}
        onAddressSelect={(address, label) =>
          handleAddressSelect(address, label)
        }
        onAddressClear={handleAddressClear}
        typeFilter="CONTRACT"
        hideFilters
        searchPlaceholder="Search address book or paste contract address"
        singleSelectionRequiresConfirm
      />
    </>
  );
}
